/**
 * BreadcrumbBuilder Utility
 * 
 * Provides utilities for building dynamic breadcrumb navigation
 * based on program and cohort data from the API.
 */

import React from 'react';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';

export interface BreadcrumbItem {
  text: string;
  href: string;
}

export interface BreadcrumbBuilderProps {
  /**
   * Program ID for linking to program details
   */
  programId?: string;
  
  /**
   * Program name from API data
   */
  programName?: string;
  
  /**
   * Cohort ID for linking to cohort details
   */
  cohortId?: string;
  
  /**
   * Cohort name from API data
   */
  cohortName?: string;
  
  /**
   * Current page name (e.g., "Create", "Edit")
   */
  currentPage?: string;
}

/**
 * Builds breadcrumb items based on the provided context
 * 
 * @param props - Breadcrumb builder configuration
 * @returns Array of breadcrumb items
 */
export function buildBreadcrumbs(props: BreadcrumbBuilderProps): BreadcrumbItem[] {
  const items: BreadcrumbItem[] = [
    { text: 'Programs', href: '/programs' }
  ];
  
  // Add program breadcrumb if program data is available
  if (props.programId && props.programName) {
    items.push({
      text: props.programName,
      href: `/programs/${props.programId}`
    });
  }
  
  // Add cohort breadcrumb if cohort data is available
  if (props.cohortId && props.cohortName) {
    items.push({
      text: props.cohortName,
      href: `/cohorts/${props.cohortId}`
    });
  }
  
  // Add current page as non-clickable item
  if (props.currentPage) {
    items.push({
      text: props.currentPage,
      href: '#'
    });
  }
  
  return items;
}

/**
 * BreadcrumbBuilder Component
 * 
 * Renders a CloudScape BreadcrumbGroup with dynamically built items
 */
export const BreadcrumbBuilder: React.FC<BreadcrumbBuilderProps> = (props) => {
  const items = buildBreadcrumbs(props);
  
  return (
    <BreadcrumbGroup
      items={items}
      ariaLabel="Breadcrumbs"
    />
  );
};

export default BreadcrumbBuilder;
